import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPriceVariationReport,
  exportPriceVariationReport,
  clearError,
} from 'store/slices/reportSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { TrendingUp, TrendingDown, Package, IndianRupee, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { fetchVendorDropdown } from 'store/slices/commonSlice';
import FormField from 'components/common/FormField';
import CategorySubcategorySelect from 'components/common/CategorySubcategorySelect';

const PriceVariationPage = () => {
  const dispatch = useDispatch();

  const {
    priceVariationReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report);

  // Get all vendors from Redux state
  const vendors = useSelector((state) => state.common?.vendors.items || []);


  const summary = priceVariationReport?.data?.summary || {};
  const results = priceVariationReport?.data?.results || [];
  const pagination = priceVariationReport?.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    vendor: 'all',
    unit_price_gte: '',
    unit_price_lte: '',
    variation_gte: '',
    variation_lte: '',
    variation_pct_gte: '',
    variation_pct_lte: '',
    trend: 'all',
       category: 'all',
    sub_category: 'all',
  });

  // Calculate total pages from server response
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'price_variation',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.vendor !== 'all' && { vendor: advancedFilters.vendor }),
      ...(advancedFilters.unit_price_gte && { unit_price__gte: advancedFilters.unit_price_gte }),
      ...(advancedFilters.unit_price_lte && { unit_price__lte: advancedFilters.unit_price_lte }),
      ...(advancedFilters.variation_gte && { variation__gte: advancedFilters.variation_gte }),
      ...(advancedFilters.variation_lte && { variation__lte: advancedFilters.variation_lte }),
      ...(advancedFilters.variation_pct_gte && { variation_pct__gte: advancedFilters.variation_pct_gte }),
      ...(advancedFilters.variation_pct_lte && { variation_pct__lte: advancedFilters.variation_pct_lte }),
      ...(advancedFilters.trend !== 'all' && { trend: advancedFilters.trend }),
       ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
    };

    dispatch(fetchPriceVariationReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all vendors on component mount
  useEffect(() => {
    dispatch(fetchVendorDropdown());
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      vendor: 'all',
      unit_price_gte: '',
      unit_price_lte: '',
      variation_gte: '',
      variation_lte: '',
      variation_pct_gte: '',
      variation_pct_lte: '',
      trend: 'all',
    });
  };

  const columns = [
    { accessorKey: 'Vendor', header: 'Vendor' },
    { accessorKey: 'Category', header: 'Category' },
    { accessorKey: 'Sub Category', header: 'Sub Category' },
    { accessorKey: 'Quantity', header: 'Quantity' },
    { accessorKey: 'Model/Brand', header: 'Model / Brand' },
    { accessorKey: 'Purchase Date', header: 'Purchase Date' },
    { accessorKey: 'Last Purchased Date', header: 'Last Purchased Date' },
    { accessorKey: 'Previous Unit Price', header: 'Previous Unit Price' },
    {
      accessorKey: 'Unit Price',
      header: 'Unit Price (₹)',
      cell: ({ row }) => (
        <span className="font-medium">
          ₹{row.original['Unit Price']?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) ?? '0.00'}
        </span>
      ),
    },
    { accessorKey: 'Total Price', header: 'Total Price' },
    {
      accessorKey: 'Variation',
      header: 'Variation (₹)',
      cell: ({ row }) => {
        const variation = row.original.Variation ?? 0;
        const isPositive = variation > 0;
        const isNegative = variation < 0;
        return (
          <span className={isPositive ? 'text-green-600' : isNegative ? 'text-destructive' : ''}>
            {isPositive && '+'}₹{Math.abs(variation).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      accessorKey: 'Variation %',
      header: 'Variation (%)',
      cell: ({ row }) => {
        const percent = row.original['Variation %'] ?? 0;
        const isPositive = percent > 0;
        const isNegative = percent < 0;
        return (
          <span className={isPositive ? 'text-green-600' : isNegative ? 'text-destructive' : ''}>
            {isPositive && '+'}{percent.toFixed(2)}%
          </span>
        );
      },
    },
    {
      accessorKey: 'Trend',
      header: 'Trend',
      cell: ({ row }) => {
        const trend = row.original.Trend;
        if (!trend) return <Badge variant="secondary">N/A</Badge>;
        if (trend === 'Increase')
          return <Badge variant="default" className="bg-green-600"><TrendingUp className="h-3 w-3 mr-1" />Increase</Badge>;
        if (trend === 'Decrease')
          return <Badge variant="destructive"><TrendingDown className="h-3 w-3 mr-1" />Decrease</Badge>;
        return <Badge variant="secondary">Stable</Badge>;
      },
    },
    { accessorKey: 'Invoice No', header: 'Invoice No' },
    { accessorKey: 'Serial No', header: 'Serial No' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Price Variation Report"
        description="Track unit price changes across vendors and models over time"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Select
                  value={advancedFilters.vendor}
                  onValueChange={(value) => handleAdvancedFilterChange('vendor', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(vendors) && vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

                   <CategorySubcategorySelect
                category={advancedFilters.category}
                subCategory={advancedFilters.sub_category}
                onCategoryChange={(value) => {
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    category: value,
                    sub_category: "all",
                  }));
                }}
                onSubCategoryChange={(value) =>
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    sub_category: value,
                  }))
                }
              />

              <FormField
                label="Vendor"
                name="vendor"
                type="select"
                value={advancedFilters.vendor}
                onChange={(e) =>
                  handleAdvancedFilterChange('vendor', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(vendors)
                    ? vendors.map((vendor) => ({
                      value: String(vendor.id),
                      label: vendor.name,
                    }))
                    : []),
                ]}
              />


              <div className="space-y-2">
                <Label>Unit Price Range (₹)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.unit_price_gte}
                    onChange={(e) => handleAdvancedFilterChange('unit_price_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.unit_price_lte}
                    onChange={(e) => handleAdvancedFilterChange('unit_price_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Variation Range (₹)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.variation_gte}
                    onChange={(e) => handleAdvancedFilterChange('variation_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.variation_lte}
                    onChange={(e) => handleAdvancedFilterChange('variation_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Variation % Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.variation_pct_gte}
                    onChange={(e) => handleAdvancedFilterChange('variation_pct_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.variation_pct_lte}
                    onChange={(e) => handleAdvancedFilterChange('variation_pct_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="trend">Trend</Label>
                <Select
                  value={advancedFilters.trend}
                  onValueChange={(value) => handleAdvancedFilterChange('trend', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select trend" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Increase">Increase</SelectItem>
                    <SelectItem value="Decrease">Decrease</SelectItem>
                    <SelectItem value="Stable">Stable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tracked Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary['Tracked Items'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Price Increases</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary['Price Increases'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Price Decreases</CardTitle>
            <TrendingDown className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary['Price Decreases'] ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading price variation data...</span>
        </div>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by vendor or model/brand..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from the server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportPriceVariationReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'price_variation',
              ...dateFilters,
              // Include advanced filters in export
              ...(advancedFilters.vendor !== 'all' && { vendor: advancedFilters.vendor }),
              ...(advancedFilters.unit_price_gte && { unit_price__gte: advancedFilters.unit_price_gte }),
              ...(advancedFilters.unit_price_lte && { unit_price__lte: advancedFilters.unit_price_lte }),
              ...(advancedFilters.variation_gte && { variation__gte: advancedFilters.variation_gte }),
              ...(advancedFilters.variation_lte && { variation__lte: advancedFilters.variation_lte }),
              ...(advancedFilters.variation_pct_gte && { variation_pct__gte: advancedFilters.variation_pct_gte }),
              ...(advancedFilters.variation_pct_lte && { variation_pct__lte: advancedFilters.variation_pct_lte }),
              ...(advancedFilters.trend !== 'all' && { trend: advancedFilters.trend }),
                ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
            ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
            }
          }))
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default PriceVariationPage;